// SeaRing — the Open-sea ocean ring beyond the board edge (docs/sea-design.md
// A5 "far water"): ONE mesh / ONE draw call for every ring tile, instead of a
// water plane instance per tile. Same look as TerrainWater's deep ocean (the
// identical vertex wave off the shared `waterTime`, crest shimmer, ripples),
// graded from the board's shallow colour to the open-sea colour so it joins
// the outer ocean tiles and the OpenSea disc seamlessly. Coast foam stays with
// the real coast tiles; the ring has no land to foam against, only faint
// whitecaps. Not pickable (raycast disabled).

import { useEffect, useMemo } from 'react';
import * as THREE from 'three';
import { waterTime } from '../TerrainWater.js';
import { buildOceanRingGeometry } from './seaGeometry.js';
import type { BoundaryEdge, RingTile } from './seaTiles.js';

const ringMaterial = new THREE.MeshBasicMaterial({
  vertexColors: true,
  fog: true,
  side: THREE.DoubleSide,
});
ringMaterial.onBeforeCompile = (shader) => {
  shader.uniforms.uTime = waterTime;
  shader.vertexShader =
    `uniform float uTime;\nvarying float vWave;\nvarying float vNear;\nvarying vec2 vWorldXZ;\n${shader.vertexShader}`.replace(
      '#include <begin_vertex>',
      `#include <begin_vertex>
      vec2 _wp = transformed.xz;
      float _w = sin(_wp.x * 2.5 + uTime) * cos(_wp.y * 2.5 + uTime * 0.7);
      _w += 0.6 * sin(_wp.x * 5.0 + uTime * 1.3) * sin(_wp.y * 5.0 - uTime * 0.9);
      _w += 0.35 * sin((_wp.x + _wp.y) * 1.1 + uTime * 0.5);
      vWave = _w;
      // 1 near the board's shallows, 0 in the open sea: detail fades so the ring melts into OpenSea.
      vNear = smoothstep(-0.53, -0.27, transformed.y);
      vWorldXZ = _wp;
      transformed.y += _w * 0.05;`,
    );
  shader.fragmentShader =
    `uniform float uTime;\nvarying float vWave;\nvarying float vNear;\nvarying vec2 vWorldXZ;\n${shader.fragmentShader}`.replace(
      '#include <color_fragment>',
      `#include <color_fragment>
      float _crest = smoothstep(0.6, 1.6, vWave);
      float _ripple = sin(vWorldXZ.x * 9.0 + uTime * 1.7) * sin(vWorldXZ.y * 8.0 - uTime * 1.3);
      // Sparse whitecaps: sines at different directions and periods, summed and
      // thresholded. A PRODUCT of two sines (the first version) peaks on a
      // regular grid, which read as a polka-dot lattice over the open sea.
      float _cap = smoothstep(0.98, 1.28,
        sin(vWorldXZ.x * 2.9 + uTime * 0.35) * 0.55
        + sin(vWorldXZ.y * 2.17 - uTime * 0.26) * 0.5
        + sin((vWorldXZ.x * 0.8 + vWorldXZ.y * 1.7) * 2.6 + uTime * 0.55) * 0.45);
      diffuseColor.rgb *= 1.0 - 0.1 * (1.0 - clamp(vWave, -1.0, 1.0) * vNear);
      diffuseColor.rgb += (vec3(0.07, 0.09, 0.1) * _crest + vec3(0.025) * max(_ripple, 0.0)) * (0.25 + 0.75 * vNear);
      diffuseColor.rgb = mix(diffuseColor.rgb, vec3(0.82, 0.9, 0.92), _cap * 0.3 * vNear);`,
    );
};
ringMaterial.customProgramCacheKey = () => 'sea-ring';

export function SeaRing({
  tiles,
  boundary,
  landDist,
  signature,
}: {
  readonly tiles: ReadonlyArray<RingTile>;
  readonly boundary: ReadonlyArray<BoundaryEdge>;
  /** Distance to the board's nearest land (shore field): the ring is coloured like the board's own water. */
  readonly landDist?: (x: number, z: number) => number;
  /** Terrain signature; the geometry is rebuilt only when this changes. */
  readonly signature: string;
}) {
  const geo = useMemo(
    () => buildOceanRingGeometry(tiles, boundary, landDist),
    [signature, tiles.length],
  );
  useEffect(() => () => geo.dispose(), [geo]);
  if (tiles.length === 0) return null;
  return (
    <mesh
      name="sea-ring"
      geometry={geo}
      material={ringMaterial}
      frustumCulled={false}
      raycast={() => null}
      renderOrder={0}
    />
  );
}
